import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import RideForm from "@/components/RideForm";
import { toast } from "sonner";

function rideToFormValues(ride) {
  if (!ride) return null;
  const dt = new Date(ride.start_datetime);
  const pad = (n) => String(n).padStart(2, "0");
  return {
    title: ride.title || "",
    description: ride.description || "",
    start_date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
    start_time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
    flexibility_date_days: ride.flexibility_date_days || 0,
    flexibility_time_minutes: ride.flexibility_time_minutes || 0,
    ride_type: ride.ride_type || "lento",
    bike_type: ride.bike_type || "bdc",
    surface_types: ride.surface_types || ["asfaltato"],
    distance_km: String(ride.distance_km ?? ""),
    elevation_m: String(ride.elevation_m ?? ""),
    pace_kmh: ride.pace_kmh ? String(ride.pace_kmh) : "",
    location: ride.location
      ? {
          name: ride.location.name,
          label: ride.location.label || ride.location.name,
          lat: ride.location.lat,
          lng: ride.location.lng,
          meeting_notes: ride.location.meeting_notes || "",
        }
      : null,
    komoot_link: ride.komoot_link || "",
    waypoints: ride.waypoints || [],
    creator_power: ride.creator_power ? String(ride.creator_power) : "",
    target_level: ride.target_level || "tutti",
  };
}

export default function EditRide() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [ride, setRide] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/rides/${id}`)
      .then((r) => setRide(r.data))
      .catch((err) => {
        const msg = formatApiError(err.response?.data?.detail) || "Giro non trovato";
        setError(msg);
        toast.error(msg);
        nav("/feed", { replace: true });
      });
  }, [id, nav]);

  const submit = async (payload) => {
    try {
      await api.put(`/rides/${id}`, payload);
      toast.success("Giro aggiornato");
      nav(`/ride/${id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (authLoading || !ride) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" />
        </div>
      </>
    );
  }

  if (error) return null;

  if (!user || user.user_id !== ride.creator_id) {
    return (
      <>
        <Navbar />
        <main className="max-w-3xl mx-auto px-4 md:px-8 py-12 text-center">
          <h1 className="font-display font-black text-2xl uppercase text-gray-900 mb-2">Accesso negato</h1>
          <p className="text-gray-500">Solo il creatore del giro può modificarlo.</p>
        </main>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="mb-8">
          <div className="label-tag mb-2">Modifica giro</div>
          <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter text-gray-900">
            Aggiorna i dettagli
          </h1>
          <p className="text-gray-500 mt-2 text-sm">I partecipanti vedranno subito le modifiche.</p>
        </div>

        <RideForm
          initialValues={rideToFormValues(ride)}
          submitLabel="Salva modifiche"
          submittingLabel="Salvataggio..."
          onSubmit={submit}
          onCancel={() => nav(`/ride/${id}`)}
          testIdPrefix="edit"
        />
      </main>
    </>
  );
}
